import { type IBase } from "@/interfaces/api/base.interface"
import { BaseModel } from "@/models/base.model"
import ModelManager from "@/services/model-manager"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"

export class ModelFactory {
  // OVERLOAD: createModel without data will always return T
  public static createModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType,
    managed?: boolean
  ): ModelType

  // OVERLOAD: createModel with data will return T | null
  public static createModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: DataType | ModelType,
    managed?: boolean
  ): ModelType | null

  public static createModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType,
    managed = true
  ): ModelType | null {
    // If unmanaged, simply create a new unmanaged instance of the model
    if (!managed) {
      return new modelType(data ? (data as DataType) : ({} as DataType), false)
    }

    // If no data is provided, create a new, managed model instance
    if (!data) {
      return ModelManager.getInstance().create(modelType)
    }

    // Data provided, update or create an instance through the ModelManager
    return ModelManager.getInstance().updateOrCreate(modelType, data)
  }

  public static createModels<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    dataList: DataType[],
    managed = true
  ): ModelType[] {
    if (managed) {
      return ModelManager.getInstance().bulkUpdateOrCreate(modelType, dataList)
    }

    return dataList.map((data) => new modelType(data, false))
  }

  // OVERLOAD: when data is a string (lazy reference), return string
  public static childModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: string,
    managed?: boolean
  ): string

  // OVERLOAD: when data is an object, return ModelType
  public static childModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType | null,
    managed?: boolean
  ): ModelType

  // OVERLOAD: when data could be either, return ModelType | string
  public static childModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType | string | null,
    managed?: boolean
  ): ModelType | string

  public static childModel<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType | string | null,
    managed = false
  ): ModelType | string {
    if (!data) {
      return undefined! as ModelType // Secret sauce to make the compiler happy
    }

    if (typeof data === "string") {
      // Preserve the UUID string for (optional) later lazy resolution
      return data
    }

    // It's an object — create a model
    return this.createModel(modelType, data, managed)
  }

  public static childModels<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    dataList: (DataType | string)[],
    managed = false
  ): ModelType[] {
    if (!dataList) {
      return []
    }

    const result: (ModelType | string)[] = []
    for (const item of dataList) {
      if (typeof item === "string") {
        // Preserve the UUID string for (optional) later lazy resolution
        result.push(item)
      } else {
        // It's an object — create a model
        result.push(this.createModel(modelType, item, managed))
      }
    }
    // The array may contain unresolved lazy-reference strings alongside model instances.
    // Consumers declare their fields as ModelType[] and rely on lazy resolution to replace
    // the strings with actual models before access. The cast keeps both sides consistent.
    return result as ModelType[]
  }
}
